import { tzOffsetMs } from '../gpthelp-tz';

/**
 * Localization of instants for the GPTHelp agent: every timestamp the model
 * sees (host context `now.local`, business-day window, tool results) is a
 * branch-local ISO string with its UTC offset, so it never has to convert.
 */

/** UTC instants as the API serializes them (`Z`, optional millis). */
const UTC_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

const MAX_DEPTH = 20;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DDTHH:mm:ss±HH:MM` of `date` as seen in `timeZone` (no millis). */
export function formatLocalIso(date: Date, timeZone: string): string {
  const offsetMs = tzOffsetMs(date, timeZone);
  const local = new Date(date.getTime() + offsetMs);

  const offsetMinutes = Math.round(offsetMs / 60_000);
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const offset = `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;

  return (
    `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}` +
    `T${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}:${pad2(local.getUTCSeconds())}` +
    offset
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function localizeDeep(value: unknown, timeZone: string, depth: number): unknown {
  if (typeof value === 'string') {
    return UTC_INSTANT_RE.test(value)
      ? formatLocalIso(new Date(value), timeZone)
      : value;
  }
  if (depth >= MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    return value.map((entry) => localizeDeep(entry, timeZone, depth + 1));
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = localizeDeep(entry, timeZone, depth + 1);
    }
    return out;
  }
  return value;
}

/**
 * Returns a copy of `value` where every string that is a UTC instant (`...Z`)
 * is rewritten with `formatLocalIso`. Walks plain objects and arrays only
 * (class instances, dates and other values are returned as-is), capped at
 * MAX_DEPTH levels. Date-only strings, cursors and non-Z strings are
 * untouched.
 */
export function localizeUtcInstantsDeep<T>(value: T, timeZone: string): T {
  return localizeDeep(value, timeZone, 0) as T;
}
